import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { GlobalService } from '../../service/global.service';
import { RealEmailValidatorDirective } from '../../directives/real-email-validator.directive';
import { TurnstileComponent } from '../../shared/turnstile/turnstile.component';

@Component({
  selector: 'app-lavora-con-noi',
  standalone: true,
  imports: [CommonModule, FormsModule, RealEmailValidatorDirective, TurnstileComponent],
  templateUrl: './lavora-con-noi.html',
  styleUrls: ['./lavora-con-noi.scss']
})
export class LavoraConNoiComponent {
  @ViewChild(TurnstileComponent) turnstile?: TurnstileComponent;
  turnstileToken = '';

  formData = {
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    ruolo: '',
    disponibilita: '',
    messaggio: ''
  };

  cvFile: File | null = null;
  cvName = '';
  cvError = '';

  privacyAccepted = false;
  showPrivacyModal = false;

  loading = false;
  successMsg = '';
  errorMsg = '';

  // limiti consigliati
  private MAX_MB = 5;
  private ALLOWED = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  constructor(
    private http: HttpClient,
    private global: GlobalService
  ) {}

  onCvChange(event: Event): void {
    this.cvError = '';
    this.cvFile = null;
    this.cvName = '';

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // controlli base
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > this.MAX_MB) {
      this.cvError = `Il file è troppo grande (max ${this.MAX_MB}MB).`;
      input.value = '';
      return;
    }

    // controllo mime (alcuni browser possono dare stringhe diverse, ma in genere ok)
    if (file.type && !this.ALLOWED.includes(file.type)) {
      this.cvError = 'Formato non valido. Carica un PDF o un file Word (.doc/.docx).';
      input.value = '';
      return;
    }

    this.cvFile = file;
    this.cvName = file.name;
  }

  onPrivacyClick(event: MouseEvent): void {
    if (!this.privacyAccepted) {
      event.preventDefault();
      this.showPrivacyModal = true;
    }
  }

  openPrivacy(event: Event): void {
    event.preventDefault();
    this.showPrivacyModal = true;
  }

  closePrivacy(): void {
    this.showPrivacyModal = false;
  }

  acceptPrivacy(): void {
    this.privacyAccepted = true;
    this.showPrivacyModal = false;
  }

  resetForm(form: NgForm): void {
    form.resetForm();
    this.formData = { nome: '', cognome: '', email: '', telefono: '', ruolo: '', disponibilita: '', messaggio: '' };

    this.cvFile = null;
    this.cvName = '';
    this.cvError = '';

    this.successMsg = '';
    this.errorMsg = '';
    // privacyAccepted lo lascio com'è (se vuoi reset totale: this.privacyAccepted = false;)
  }

  onSubmit(form: NgForm): void {
    if (this.loading) return;
    this.successMsg = '';
    this.errorMsg = '';

    if (!this.privacyAccepted) {
      this.errorMsg = 'Devi accettare la Privacy Policy.';
      return;
    }

    if (form.pending) {
      this.errorMsg = 'Attendi un momento: stiamo verificando il dominio dell’email.';
      return;
    }

    if (form.invalid) {
      this.errorMsg = 'Controlla i campi evidenziati.';
      return;
    }

    if (!this.cvFile) {
      this.errorMsg = 'Allega il CV per inviare la candidatura.';
      return;
    }
    if (!this.turnstileToken) { this.errorMsg = 'Completa la verifica di sicurezza.'; return; }

    const fd = new FormData();
    fd.append('nome', this.formData.nome);
    fd.append('cognome', this.formData.cognome);
    fd.append('email', this.formData.email);
    fd.append('telefono', this.formData.telefono);
    fd.append('ruolo', this.formData.ruolo || '');
    fd.append('disponibilita', this.formData.disponibilita || '');
    fd.append('messaggio', this.formData.messaggio || '');
    fd.append('cv', this.cvFile, this.cvFile.name);
    fd.append('turnstile_token', this.turnstileToken);

    const url = `${this.global.url}email/work-with-us`;

    this.loading = true;

    this.http.post<{ confirmationSent?: boolean }>(url, fd)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.resetForm(form);
          this.successMsg = 'Candidatura inviata! Ti contatteremo al più presto.';
          if (response.confirmationSent === false) this.successMsg += ' La conferma email non è stata inviata, ma abbiamo ricevuto la candidatura: non occorre inviarla di nuovo.';
          this.turnstileToken = '';
          this.turnstile?.reset();
        },
        error: (err) => {
          const fields = err?.error?.fields;
          this.errorMsg = fields ? String(Object.values(fields)[0]) : (err?.error?.error || 'Errore durante l’invio. Riprova oppure contattaci via telefono.');
          this.turnstile?.reset();
        }
      });
  }
}
